/**
 * domainRegistry — `domain → DomainInterpreter`. **The single extension point.**
 *
 * Supporting a new backend domain is one `register` call plus its interpreter
 * file, and zero changes to the parser, the reducer, the store, the FSM, the
 * transport, or any component. That property is the entire reason the event
 * surface was normalised into `(domain, phase)` instead of handled per event name.
 */

import type { DomainInterpreter } from 'src/content/ChatV2/steps/model/types';
import { EVENT_DOMAIN } from 'src/content/ChatV2/steps/wire/eventType';

import { interactionDomain } from './interactionDomain';
import { messageDomain } from './messageDomain';
import { runDomain, usageDomain } from './nonStepDomain';
import { toolDomain } from './toolDomain';

const registry = new Map<string, DomainInterpreter>();

/** Register (or replace) the interpreter for a domain. */
export function registerDomain(
  interpreter: DomainInterpreter,
  ...aliases: string[]
): void {
  registry.set(interpreter.domain, interpreter);
  for (const alias of aliases) {
    registry.set(alias, interpreter);
  }
}

/**
 * The interpreter for a domain, or `undefined` when none is registered.
 */
export function getDomainInterpreter(
  domain: string
): DomainInterpreter | undefined {
  return registry.get(domain);
}

export function registeredDomains(): string[] {
  return [...registry.keys()].sort();
}

/** Test teardown / HMR. */
export function resetDomainRegistry(): void {
  registry.clear();
  registerBuiltInDomains();
}

/**
 * Built-ins. Only the domains the backend actually emits today.
 */
export function registerBuiltInDomains(): void {
  registerDomain(toolDomain, EVENT_DOMAIN.RETRIEVAL);
  registerDomain(messageDomain);
  registerDomain(interactionDomain);
  registerDomain(runDomain);
  registerDomain(usageDomain);
}

registerBuiltInDomains();
